import { useState, useEffect, useCallback, useRef } from 'react';
import { Event, EventStatus } from '../types/types';
import { Market, MarketOutcome, MarketStatus } from '../types/marketTypes';
import { MetaMarketDTO } from '../types/api';
import { eventService } from '../services/eventService';
import { marketService, convertDTOtoMarket } from '../services/marketService';
import { websocketService } from '../services/websocketService';
import { WS_BASE_URL, WS_TOPICS } from '../config/websocket';
import { allResolved, EVENT_POLL_MILLIS, isEventLive, MARKETS_POLL_MILLIS } from '../utils/livePolling';

interface UseEventDetailsResult {
  event: Event | null;
  markets: Market[];
  isLoading: boolean;
  isMarketsLoading: boolean;
  isActionLoading: boolean;
  error: Error | null;
  refreshEvent: () => Promise<void>;
  refreshMarkets: () => Promise<void>;
  cancelEvent: () => Promise<void>;
  settleMarket: (marketId: number, winningOutcomeId: number) => Promise<void>;
}

export const useEventDetails = (eventId: number): UseEventDetailsResult => {
  const [event, setEvent] = useState<Event | null>(null);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMarketsLoading, setIsMarketsLoading] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState<boolean>(false);
  
  // Use ref to keep track of current markets to avoid stale closures in WebSocket callbacks
  const marketsRef = useRef<Market[]>([]);
  
  // Update the ref whenever markets change
  useEffect(() => {
    marketsRef.current = markets;
  }, [markets]);

  const fetchEvent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedEvent = await eventService.getEventById(eventId);
      setEvent(fetchedEvent);
    } catch (err) {
      console.error('Error loading event details:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  // Fetch event data without showing loading state (for background updates)
  const fetchEventScores = useCallback(async () => {
    try {
      const fetchedEvent = await eventService.getEventById(eventId);
      setEvent(fetchedEvent);
    } catch (err) {
      console.error('Error updating event scores:', err);
      // Don't set error state for background updates to avoid UI disruption
    }
  }, [eventId]);

  const fetchMarkets = useCallback(async (showLoading = true) => {
    if (!eventId) return;
    
    try {
      if (showLoading) {
        setIsMarketsLoading(true);
      }
      const fetchedMarkets = await marketService.getMarketsByEventId(eventId);
      
      // Compare with current markets to detect price changes
      setMarkets(currentMarkets => {
        // For each fetched market, compare with current market if it exists
        const updatedMarkets = fetchedMarkets.map(fetchedMarket => {
          // Find corresponding market in current state
          const currentMarket = currentMarkets.find(m => m.id === fetchedMarket.id);
          
          if (!currentMarket) return fetchedMarket; // New market, no comparison needed
          
          // Compare prices and mark changes
          fetchedMarket.selections.forEach(fetchedSelection => {
            const currentSelection = currentMarket.selections.find(s => s.id === fetchedSelection.id);
            
            if (currentSelection) {
              fetchedSelection.prices.forEach(fetchedPrice => {
                const currentPrice = currentSelection.prices.find(p => p.id === fetchedPrice.id);
                
                if (currentPrice) {
                  // Compare price values and set the change direction
                  if (fetchedPrice.price > currentPrice.price) {
                    fetchedPrice.priceChange = 'up';
                  } else if (fetchedPrice.price < currentPrice.price) {
                    fetchedPrice.priceChange = 'down';
                  } else {
                    // Keep existing price change indicator if present
                    fetchedPrice.priceChange = currentPrice.priceChange;
                  }
                }
              });
            }
          });
          
          return fetchedMarket;
        });
        
        // Schedule clearing the price change indicators after 3 seconds
        setTimeout(() => {
          setMarkets(markets => {
            return markets.map(market => ({
              ...market,
              selections: market.selections.map(selection => ({
                ...selection,
                prices: selection.prices.map(price => ({
                  ...price,
                  priceChange: null
                }))
              }))
            }));
          });
        }, 3000); // Clear indicators after 3 seconds
        
        return updatedMarkets;
      });
    } catch (err) {
      console.error('Error loading markets:', err);
      setError(err as Error);
    } finally {
      if (showLoading) {
        setIsMarketsLoading(false);
      }
    }
  }, [eventId]);

  // Handle real-time market updates via WebSocket
  const handleMarketUpdate = useCallback((marketDto: MetaMarketDTO) => {
    console.log('Received market update via WebSocket:', marketDto);
    
    // Convert the DTO to our frontend Market type
    const updatedMarket = convertDTOtoMarket(marketDto, eventId);
    
    setMarkets(currentMarkets => {
      // Find if this market already exists in our state
      const marketIndex = currentMarkets.findIndex(m => m.id === updatedMarket.id);
      
      if (marketIndex >= 0) {
        // Get the existing market to compare prices
        const existingMarket = currentMarkets[marketIndex];
        
        // Compare prices and mark changes
        updatedMarket.selections.forEach(updatedSelection => {
          const existingSelection = existingMarket.selections.find(s => s.id === updatedSelection.id);
          
          if (existingSelection) {
            updatedSelection.prices.forEach(updatedPrice => {
              const existingPrice = existingSelection.prices.find(p => p.id === updatedPrice.id);
              
              if (existingPrice) {
                // Compare price values and set the change direction
                if (updatedPrice.price > existingPrice.price) {
                  updatedPrice.priceChange = 'up';
                } else if (updatedPrice.price < existingPrice.price) {
                  updatedPrice.priceChange = 'down';
                } else {
                  updatedPrice.priceChange = null;
                }
              }
            });
          }
        });
        
        // Update existing market
        const newMarkets = [...currentMarkets];
        newMarkets[marketIndex] = updatedMarket;
        
        // Schedule clearing the price change indicators after 3 seconds
        setTimeout(() => {
          setMarkets(currentMarkets => {
            // Find the market again (it might have changed)
            const marketIndex = currentMarkets.findIndex(m => m.id === updatedMarket.id);
            if (marketIndex >= 0) {
              const marketCopy = {...currentMarkets[marketIndex]};
              
              // Clear price change indicators
              marketCopy.selections = marketCopy.selections.map(selection => ({
                ...selection,
                prices: selection.prices.map(price => ({
                  ...price,
                  priceChange: null
                }))
              }));
              
              const newMarkets = [...currentMarkets];
              newMarkets[marketIndex] = marketCopy;
              return newMarkets;
            }
            return currentMarkets;
          });
        }, 3000); // Clear indicators after 3 seconds
        
        return newMarkets;
      } else {
        // Add new market
        return [...currentMarkets, updatedMarket];
      }
    });
  }, [eventId]);
  
  // Connect to WebSocket and subscribe to market updates
  useEffect(() => {
    if (!eventId) return;
    
    console.log(`Setting up WebSocket connection for event ID: ${eventId}`);
    console.log('WebSocket URL:', WS_BASE_URL);
    console.log('WebSocket topics:', {
      eventTopic: WS_TOPICS.MARKET_UPDATES(eventId),
      allMarketsTopic: WS_TOPICS.ALL_MARKETS
    });
    
    // Connect to WebSocket
    websocketService.connect();
    setIsWebSocketConnected(true);
    
    // Subscribe to market updates for this event
    websocketService.subscribeToMarketUpdates(eventId, handleMarketUpdate);
    
    // Also subscribe to the global markets topic
    websocketService.subscribeToAllMarketUpdates((marketDto) => {
      // Only process updates for this event
      if (marketDto.eventId === eventId) {
        handleMarketUpdate(marketDto);
      }
    });
    
    // Cleanup: unsubscribe when component unmounts or eventId changes
    return () => {
      console.log(`Cleaning up WebSocket subscriptions for event ID: ${eventId}`);
      websocketService.unsubscribeFromMarketUpdates(eventId);
      websocketService.unsubscribeFromAllMarketUpdates();
    };
  }, [eventId, handleMarketUpdate]);
  
  // Load event details and markets when the component mounts
  useEffect(() => {
    let cancelled = false;
    const initializeData = async () => {
      try {
        const fetchedEvent = await eventService.getEventById(eventId);
        if (cancelled || !fetchedEvent) return;
        setEvent(fetchedEvent);

        const fetchedMarkets = await marketService.getMarketsByEventId(eventId);
        if (!cancelled) setMarkets(fetchedMarkets);
      } catch (error) {
        console.error('Error loading event details:', error);
      }
    };

    initializeData();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // The moment this page saw every market get its outcome; unset while any is still unresolved
  const resolvedAtRef = useRef<Date | undefined>(undefined);
  const sawUnresolvedRef = useRef(false);
  if (!allResolved(markets)) {
    sawUnresolvedRef.current = markets.length > 0;
    resolvedAtRef.current = undefined;
  } else if (sawUnresolvedRef.current && !resolvedAtRef.current) {
    resolvedAtRef.current = new Date();
  }

  const isLoaded = event !== null;
  const isLive = isLoaded && isEventLive(event?.startTime, markets, new Date(), resolvedAtRef.current);
  const wasLiveRef = useRef(false);

  // Poll markets and the score while the event is live; each request waits for the previous answer
  useEffect(() => {
    if (!isLive) return;
    let cancelled = false;
    const timers: NodeJS.Timeout[] = [];

    const poll = (refresh: () => Promise<void>, intervalMillis: number) => {
      const tick = async () => {
        try {
          await refresh();
        } catch (err) {
          console.error('Error refreshing event details:', err);
        }
        if (!cancelled) timers.push(setTimeout(tick, intervalMillis));
      };
      timers.push(setTimeout(tick, intervalMillis));
    };

    poll(async () => {
      const fetchedMarkets = await marketService.getMarketsByEventId(eventId);
      if (!cancelled) setMarkets(fetchedMarkets);
    }, MARKETS_POLL_MILLIS);
    poll(async () => {
      const fetchedEvent = await eventService.getEventById(eventId);
      if (!cancelled && fetchedEvent) setEvent(fetchedEvent);
    }, EVENT_POLL_MILLIS);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [eventId, isLive]);

  // Read the score once more when the event stops being live, so the final state is on screen
  useEffect(() => {
    if (wasLiveRef.current && !isLive) {
      fetchEventScores();
    }
    wasLiveRef.current = isLive;
  }, [isLive, fetchEventScores]);

  const cancelEvent = async () => {
    if (!event) return;
    
    if (window.confirm('Are you sure you want to void all markets for this event?')) {
      try {
        setIsActionLoading(true);
        await marketService.voidEventMarkets(event.id);
        await fetchMarkets();
      } catch (err) {
        console.error('Failed to void event markets:', err);
        setError(err as Error);
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const settleMarket = async (marketId: number, winningOutcomeId: number) => {
    try {
      setIsActionLoading(true);
      await marketService.settleMarket(marketId, winningOutcomeId);
      await fetchMarkets(); // Refresh markets after settlement
    } catch (err) {
      console.error('Failed to settle market:', err);
      setError(err as Error);
    } finally {
      setIsActionLoading(false);
    }
  };

  return {
    event,
    markets,
    isLoading,
    isMarketsLoading,
    isActionLoading,
    error,
    refreshEvent: fetchEvent,
    refreshMarkets: fetchMarkets,
    cancelEvent,
    settleMarket
  };
};
